import instance from "~/utils/i18n";
import { type ProductData, type PublicationDownloadFile } from "~/domains/product/types/ProductData";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import * as PriceUtils from "~/utils/PriceUtils";
import { sum } from "~/utils/PriceUtils";
import { CURRENCY_ISO, INVALID_PACKAGE_CODE } from "~/utils/constants";
import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type GAPageTypeName, SearchPageType } from "~/features/search/models/SearchPageType";
import { getSearchPageType } from "~/features/search/utils/searchPageUtils";
import {
  getNetPrice,
  getPricePerPiece,
  getTotalPriceForLineItem,
  makeDescriptorFromFullState,
} from "~/domains/product/utils/productDescriptorUtils";
import { RecommendedProductsService } from "~/features/productDetails/RecommendedProducts/recommendedProductsService";
import {
  trackUserAddRecommendedProductPurchased,
  trackUserAddRecommendedProductToWishlist,
} from "~/features/productDetails/RecommendedProducts/recommendedProductAnalytics";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { PAYMENT_TYPE_2_TRANSLATION_KEY } from "~/features/cart/checkout/components/CheckoutSummary/constants";
import { getCampaignCodesFromProduct } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { getScanStatus, type ScanResultType } from "~/features/scanDmc/types/ScanDMC";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";

import { isNonNull } from "~/common/utils/reducers";
import { CartMethods, type SalesPartnerStock } from "~/app/analytics/ga/salesPartner";

import type { ProductImpression } from "~/app/analytics/ga/types";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { wrap } from "~/utils/lineItemHelper";

interface GAEvent {
  event: string;
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  push_context?: string;

  [label: string]:
    | string
    | undefined
    | {
        min: string;
        max: string;
      };
}

interface GAEcommerceEvent {
  event: string;
  // @ts-ignore
  paymentType?: {
    code: string;
    displayName: string;
  };
  ecommerce: {
    currencyCode: string;
    click?: {
      actionField: { list?: string; step?: number };
      products: ProductImpression[];
    };
    add?: {
      products: ProductImpression[];
    };
    detail?: {
      products: ProductImpression[];
    };
    filterUsage?: string;
    purchase?: {
      actionField: {
        id: string;
        revenue: string;
      };
      products: ProductImpression[];
    };
    checkout?: {
      actionField: { step: number };
      products: ProductImpression[];
    };
    remove?: {
      products: ProductImpression[];
    };
    impressions?: ProductImpression[];
  };

  [label: string]:
    | string
    | {
        currencyCode: string;
        click?: {
          actionField: { list?: string; step?: number };
          products: ProductImpression[];
        };
        add?: {
          products: ProductImpression[];
        };
      }
    | {
        code: string;
        displayName: string;
      };
}

interface AffilateEvent {
  event: "affiliate_data";
  affiliate_id: string;
  affiliate_name: string;
  sales_partner_status: "saved permanently" | "saved temporarily" | "not saved";
}

interface VirtualPageView {
  event: "virtualPageView";
  virtualPagePath: string;
}

const ASSISTANT_PRODUCT_TYPES: PurchaseType[] = ["bsa", "hsa", "lsa", "htsa"];
const RECOMMENDATION_PRODUCT_TYPES: PurchaseType[] = [
  "rmp",
  "rp",
  "pp",
  "ap",
  "ppr",
  "rsp",
  "top",
  "spos",
  "cob",
  "fbt",
  "rfy",
  "ric",
];

// eslint-disable-next-line
export function sendEvent(data: GAEvent | GAEcommerceEvent | AffilateEvent | VirtualPageView | any) {
  if (window.dataLayer) {
    // console.log(data);
    window.dataLayer.push(data);
  }
}

export const sendGAEvent = (data: Omit<GAEvent, "event">) => sendEvent({ ...data, event: "gaEvent" } as GAEvent);

export function sendNavigationLinkEvent(label: string, level: number) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "Navigation",
    eventAction: `Level ${level}`,
    eventLabel: label,
  });
}

export function sendSearchBoxSearch(keyword: string) {
  sendEvent({
    event: "gaEvent",
    searchKeyword: keyword,
    eventCategory: "Search",
    eventAction: keyword,
    eventLabel: "Type",
  });
}

export function sendSearchBoxAutosuggestClick(keyword: string, designation: string) {
  sendEvent({
    event: "gaEvent",
    searchKeyword: designation,
    eventCategory: "Search",
    eventAction: designation,
    eventLabel: "Selection",
  });
}

export function sendSearchSortChoice(sortLabelKey: string) {
  const label = instance.t(`ga.search.${sortLabelKey}`, { lng: "en" });
  sendEvent({
    event: "gaEvent",
    eventCategory: "PLP & Search Interactions",
    eventAction: "Sort by",
    eventLabel: label,
  });
}

export function sendValueFilterUsage(filtersInUse: string[], filterCategoryIndex: number) {
  let valueFilterCategory = "";
  switch (filterCategoryIndex) {
    case 0: {
      valueFilterCategory = "Filter Dimension";
      break;
    }
    case 1: {
      valueFilterCategory = "Filter Load Rating";
      break;
    }
    default: {
      valueFilterCategory = "Filter missing category";
    }
  }
  const labels = filtersInUse.map((code) => instance.t(`ga.search.${code}`, { lng: "en" }));
  labels.sort();
  sendEvent({
    event: "gaEvent",
    eventCategory: "PLP & Search Interactions",
    eventAction: valueFilterCategory,
    eventLabel: labels.join(", "),
  });
}

export function sendCharacteristicsUsage(characteristicsInUse: string[]) {
  const labels = characteristicsInUse.map((code) => instance.t(`ga.search.${code}`, { lng: "en" }));
  labels.sort();
  sendEvent({
    event: "gaEvent",
    eventCategory: "PLP & Search Interactions",
    eventAction: "Filter Characteristics",
    eventLabel: labels.join(", "),
  });
}

export function trackSrpAddToWishlist(designation: string, step: number) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PLP & Search Interactions",
    eventAction: `Add to Wishlist - Step ${step}`,
    eventLabel: designation,
  });
}

export function sendSrpProductListImpression(
  impressions: ProductImpression[],
  hasFilters: boolean,
  hasNonDefaultSort: boolean,
) {
  let filterUsage = "";

  if (hasFilters) {
    if (hasNonDefaultSort) {
      filterUsage = "Filter & Sort applied";
    } else {
      filterUsage = "Filter applied";
    }
  } else {
    if (hasNonDefaultSort) {
      filterUsage = "Sort applied";
    } else {
      filterUsage = "No configuration";
    }
  }

  sendEvent({
    event: "productImpression",
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      filterUsage,
      impressions,
    },
  });
}

export function sendWishlistProductListImpression(impressions: ProductImpression[]) {
  sendSrpProductListImpression(impressions, false, false);
}

export function sendWishlistProductClick(baseData: ProductData, selection: string, position: number) {
  const impression = convertProductToImpression({ basic: baseData, position });

  sendEvent({
    event: "productClick",
    filterUsage: "No configuration",
    productClick_selection: selection,
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      click: {
        actionField: {
          list: "Wishlist",
        },
        products: [impression],
      },
    },
  });
}

export function sendWishlistAddSelectedToCartClick(data: { product: ProductData; index: number }[]) {
  const impressions = data.map(({ product, index }) => convertProductToImpression({ basic: product, position: index }));

  sendEvent({
    event: "productClick",
    filterUsage: "No configuration",
    productClick_selection: "Select",
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      click: {
        actionField: {
          list: "Wishlist",
        },
        products: impressions,
      },
    },
  });
}

export function sendSrpProductClick(
  baseData: ProductData,
  hasFilters: boolean,
  hasNonDefaultSort: boolean,
  selection: string,
  position: number,
) {
  let filterUsage = "";

  if (hasFilters) {
    if (hasNonDefaultSort) {
      filterUsage = "Filter & Sort applied";
    } else {
      filterUsage = "Filter applied";
    }
  } else {
    if (hasNonDefaultSort) {
      filterUsage = "Sort applied";
    } else {
      filterUsage = "No configuration";
    }
  }

  const pageType = getSearchPageType();

  const impression = convertProductToImpression({ basic: baseData, position });

  sendEvent({
    event: "productClick",
    filterUsage,
    productClick_selection: selection,
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      click: {
        actionField: {
          // TODO - Other cases? Campaign!
          list: pageType === SearchPageType.PLP ? window.SCHA.pageCategory : "Search Results",
        },
        products: [impression],
      },
    },
  });
}

export function convertFixedPriceProductToImpression(data: {
  basic: ProductData;
  price?: PriceData;
  availability?: AvailabilityInfo;
  packageCode?: string;
  packageSize?: number;
  quantity?: number;
  position?: number;
  productRecommendation?: {
    place: PurchasePlace;
    type: PurchaseType;
  };
}) {
  const { basic, price, availability, packageCode, packageSize } = data;

  let priceValue = "";
  let priceWithSymbol = "";
  if (price) {
    priceValue = price.value.toString();
    priceWithSymbol = PriceUtils.nonVolatileText(price);
  }

  let brand = "";
  if (basic.manufacturerLogoUrl && basic.manufacturerLogoUrl.indexOf("FAG") !== -1) {
    brand = "FAG";
  }
  if (basic.manufacturerLogoUrl && basic.manufacturerLogoUrl.indexOf("INA") !== -1) {
    brand = "INA";
  }

  const salesCodes = getCampaignCodesFromProduct(basic.salesCampaign);
  const isAssistantProduct =
    !!data.productRecommendation?.type && ASSISTANT_PRODUCT_TYPES.includes(data.productRecommendation.type);

  const isRecommendationProduct =
    !!data.productRecommendation?.type && RECOMMENDATION_PRODUCT_TYPES.includes(data.productRecommendation.type);

  const impression: ProductImpression = {
    name: basic.designation,
    id: basic.sapMaterialNumber,
    price: priceValue,
    brand,
    category: basic.productAllCategories.join(", "),
    variant: packageSize !== undefined ? `${packageSize} per pack` : "",
    dimension10: packageCode ? basic.sapMaterialNumber + packageCode : "",
    dimension11: priceWithSymbol,
    dimension12: availability ? (selectProductIsInStock(availability) ? "In Stock" : "Not In Stock") : "Unknown",
    dimension13: basic.preferred ? "Preferred" : "Non-Preferred",
    dimension14: basic.productLineId,
    dimension15: basic.baseType,
    is_alternative_product_recommendation: RecommendedProductsService.isProductRecommendedForSession(
      basic.designation,
    ).toString(),

    //grab first entry of sales codes, only one campaign active at a time
    campaign: salesCodes[0],
    is_campaign_belonging: !!salesCodes.length,

    is_reco_product: isRecommendationProduct,
    reco_type: isRecommendationProduct ? data.productRecommendation?.type : undefined,
    reco_place: isRecommendationProduct ? data.productRecommendation?.place : undefined,
    is_assistant_product: isAssistantProduct,
    assistant_type: isAssistantProduct ? data.productRecommendation?.type : undefined,
  };

  if (data.quantity !== undefined) {
    impression.quantity = data.quantity;
  }

  if (data.position !== undefined) {
    impression.position = data.position;
  }

  return impression;
}

export function convertProductToImpression(data: {
  basic: ProductData;
  price?: PriceInfo;
  availability?: AvailabilityInfo;
  packageCode?: string;
  packageSize?: number;
  quantity?: number;
  position?: number;
}): ProductImpression {
  const { price } = data;

  const qty = data.quantity !== undefined ? data.quantity : 1;

  let selectedPrice = undefined;
  if (price) {
    const priceArr = [data.price].filter(isNonNull);
    const product = makeDescriptorFromFullState(
      data.basic,
      { state: priceArr.length ? "loaded" : "loading", data: priceArr },
      { state: data.availability ? "loaded" : "loading", data: data.availability },
      getInitialPriceReference(), // TODO: Unknown price reference
    );
    selectedPrice =
      getNetPrice({ totalQuantity: qty, product, packCode: data.packageCode ?? INVALID_PACKAGE_CODE }) ?? undefined;
  }

  return convertFixedPriceProductToImpression({
    ...data,
    price: selectedPrice,
  });
}

export function trackPdpLoad(
  basic: ProductData,
  price: PriceInfo | undefined,
  availability: AvailabilityInfo | undefined,
) {
  sendEvent({
    event: "productDetailView",
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      detail: {
        products: [
          convertProductToImpression({
            basic: basic,
            price,
            availability,
          }),
        ],
      },
    },
  });
}

export function trackPdpClick(label: string, designation: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: label,
    eventLabel: designation,
  });
}

export function trackPdpRelatedProductsClick(mainProductDesignation: string, relatedProductDesignation: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "Related products",
    eventLabel: `${mainProductDesignation}>${relatedProductDesignation}`,
  });
}

export function trackPdpWishlistClick(designation: string, step: number) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: `Add to Wishlist - Step ${step}`,
    eventLabel: designation,
  });
  if (RecommendedProductsService.isProductRecommendedForSession(designation)) {
    trackUserAddRecommendedProductToWishlist(designation);
  }
}

export function trackCadDataClick(format: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: `CAD Download`,
    eventLabel: format,
  });
}

export function convertProductToImpressionCart(
  basic: ProductData,
  packEntry: LineItem,
  availability: AvailabilityInfo | undefined,
  pricePerPiece: PriceData | undefined,
): ProductImpression {
  const qty = packEntry.quantity;

  let priceValue = "";
  let priceWithSymbol = "";

  if (pricePerPiece) {
    priceValue = pricePerPiece.value.toString();
    priceWithSymbol = PriceUtils.nonVolatileText(pricePerPiece);
  }

  let brand = "";
  if (basic.manufacturerLogoUrl && basic.manufacturerLogoUrl.indexOf("FAG") !== -1) {
    brand = "FAG";
  }
  if (basic.manufacturerLogoUrl && basic.manufacturerLogoUrl.indexOf("INA") !== -1) {
    brand = "INA";
  }

  const salesCodes = getCampaignCodesFromProduct(basic.salesCampaign);

  const isAssistantProduct =
    !!packEntry.recoPlace && ASSISTANT_PRODUCT_TYPES.includes(packEntry.recoType as PurchaseType);

  const isRecommendationProduct =
    !!packEntry.recoType && RECOMMENDATION_PRODUCT_TYPES.includes(packEntry.recoType as PurchaseType);

  return {
    name: basic.designation,
    id: basic.sapMaterialNumber,
    price: priceValue,
    quantity: qty,
    brand,
    category: basic.productAllCategories.join(", "),
    variant: `${packEntry.packSize} per pack`,
    dimension10: basic.sapMaterialNumber + packEntry.packCode,
    dimension11: priceWithSymbol,
    dimension12: availability ? (selectProductIsInStock(availability) ? "In Stock" : "Not In Stock") : "",
    dimension13: basic.preferred ? "Preferred" : "Non-Preferred",
    dimension14: basic.productLineId,
    dimension15: basic.baseType,
    is_alternative_product_recommendation: RecommendedProductsService.isProductRecommendedForSession(
      basic.designation,
    ).toString(),

    campaign: salesCodes[0],
    is_campaign_belonging: !!salesCodes.length,

    is_reco_product: isRecommendationProduct,
    reco_type: isRecommendationProduct ? packEntry.recoType : undefined,
    reco_place: isRecommendationProduct ? packEntry.recoPlace : undefined,
    is_assistant_product: isAssistantProduct,
    assistant_type: isAssistantProduct ? packEntry.recoType : undefined,
  };
}

export function trackCartLoad(
  entries: LineItem[],
  productByCode: { [p: string]: ProductData },
  isCheckout?: boolean,
  paymentType?: string,
) {
  const products = entries.map((entry) => {
    const product = productByCode[entry.productCode];
    return convertProductToImpressionCart(product, entry, undefined, getPricePerPiece(entry));
  });

  const event = {
    event: "checkout",
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      checkout: {
        actionField: { step: isCheckout ? 2 : 1 },
        products,
      },
    },
  } as GAEcommerceEvent;

  if (paymentType) {
    const paymentTypeLabel = instance
      .t(PAYMENT_TYPE_2_TRANSLATION_KEY[paymentType as keyof typeof PAYMENT_TYPE_2_TRANSLATION_KEY], { lng: "en" })
      .replace("-", "")
      .trim();
    event.paymentType = {
      code: paymentType,
      displayName: paymentTypeLabel,
    };
  }

  sendEvent(event);
}

AnalyticsMQ.instance.subscribe("placeOrder", ({ entries, salesPartner, type, paymentType, orderId }) => {
  if (!(type === "purchase" || type === "quote")) {
    return;
  }

  const impressions = entries.map(({ lineItem, product }) => {
    const helper = wrap(lineItem);
    return convertFixedPriceProductToImpression({
      basic: product.base,
      packageCode: lineItem.packCode,
      packageSize: lineItem.packSize,
      price: getPricePerPiece(lineItem),
      quantity: lineItem.quantity,

      productRecommendation: helper.getProductRecommendation(),
    });
  });

  function isRecommendedProduct({ name }: ProductImpression) {
    return RecommendedProductsService.isProductRecommendedForSession(name);
  }

  impressions.filter(isRecommendedProduct).forEach(({ name, quantity }) => {
    trackUserAddRecommendedProductPurchased(name, quantity ?? 0);
  });

  const totalPrice = PriceUtils.sum(
    ...entries.map(({ lineItem, product }) => getTotalPriceForLineItem(lineItem, product.base)),
  );

  let eventType: "Partner_Purchase" | "quote" | "purchase";
  if (salesPartner) {
    eventType = "Partner_Purchase";
  } else {
    eventType = type;
  }

  let paymentObject = null;

  if (paymentType) {
    const paymentTypeLabel = instance
      .t(PAYMENT_TYPE_2_TRANSLATION_KEY[paymentType as keyof typeof PAYMENT_TYPE_2_TRANSLATION_KEY], { lng: "en" })
      .replace("-", "")
      .trim();

    paymentObject = {
      code: paymentType,
      displayName: paymentTypeLabel,
    };
  }

  sendEvent({
    event: eventType,
    paymentType: paymentObject,
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      purchase: {
        actionField: {
          id: orderId,
          revenue: totalPrice.value.toString(),
          // tax: '',
          // shipping: '',
        },
        products: impressions,
      },
    },
    selected_sales_partner: salesPartner,
  });
});

// Kameleoon
AnalyticsMQ.instance.subscribe("placeOrder", ({ entries, type }) => {
  if (!(type === "purchase" || type === "quote")) {
    return;
  }
  const totalValue = sum(...entries.map(({ lineItem, product }) => getTotalPriceForLineItem(lineItem, product)));
  const totalVolume = entries.map(({ lineItem }) => lineItem.quantity).reduce((a, b) => a + b, 0);

  sendEvent({
    event: "kameleoon_purchase",
    totalValue: window.SCHA.currencyExchangeRate * totalValue.value,
    currency: "EUR",
    totalVolume,
    products: entries.map(({ lineItem, product }) => {
      return {
        product: product.base.designation,
        quantity: lineItem.quantity,
      };
    }),
  });
});

export function trackRemoveFromCart(products: ProductImpression[]) {
  sendEvent({
    event: "c",
    ecommerce: {
      currencyCode: CURRENCY_ISO,
      remove: {
        products,
      },
    },
  });
}

export function trackCartChange(oldImpression: ProductImpression, newImpression: ProductImpression) {
  if (
    // price per item
    oldImpression.dimension11 !== newImpression.dimension11 ||
    oldImpression.variant !== newImpression.variant
  ) {
    if (oldImpression.quantity !== 0) {
      // Remove old impression add new one
      sendEvent({
        event: "changeCart",
        ecommerce: {
          currencyCode: CURRENCY_ISO,
          remove: {
            products: [oldImpression],
          },
        },
      });
    }
    sendEvent({
      event: "changeCart",
      ecommerce: {
        currencyCode: CURRENCY_ISO,
        add: {
          products: [newImpression],
        },
      },
    });
  } else {
    // need to add or remove the difference
    let difference = newImpression.quantity! - oldImpression.quantity!;
    let mode;
    if (difference > 0) {
      mode = "add";
    } else if (difference < 0) {
      mode = "remove";
      difference = -difference;
    }
    if (mode) {
      const impression = {
        ...newImpression,
        quantity: difference,
      };
      sendEvent({
        event: "changeCart",
        method: mode === "add" ? CartMethods.CART_INCREASE : CartMethods.CART_DECREASE,
        ecommerce: {
          currencyCode: CURRENCY_ISO,
          [mode]: {
            products: [impression],
          },
        },
      });
    }
  }
}

export function trackConfigureAndQuoteClick() {
  sendEvent({
    event: "PDP_Related_Interaction",
    action: "Configure & Quote Intended",
    raw_action: "Click",
  });
}

export function trackRequestAndQuoteClick() {
  sendEvent({
    event: "PDP_Related_Interaction",
    action: "Quote Request Intended",
    raw_action: "Click",
  });
}

export function trackPdpTabChange(tabName: string) {
  sendEvent({
    event: "PDP_Related_Interaction",
    action: `${tabName} Accessed`,
    raw_action: "Click",
  });
}

export function trackPdpTabActivation(tabName: string, open: boolean) {
  sendEvent({
    event: "Tab Click",
    eventCategory: tabName,
    eventAction: open ? "Open" : "Close",
    isUserLoggedIn: "" + userIsLoggedIn(),
  });
}
export function trackPdpRequestCADNotify() {
  sendEvent({
    event: "CAD Register Notify Button",
    action: "Click",
    isUserLoggedIn: "" + userIsLoggedIn(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
}
export function trackPdpRequestCADNotifyResult(success: boolean) {
  sendEvent({
    event: "CAD Register Notify Button",
    action: "Response",
    status: success ? "Success" : "Failure",
    isUserLoggedIn: "" + userIsLoggedIn(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
}

export function trackPlpTabActivation(tabName: string, pageCategory: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PLP & Search Interactions",
    eventAction: "PLP - " + tabName,
    eventLabel: pageCategory,
  });
}

export function trackCustomerInquiry(topicKey: string) {
  const label = instance.t(`ga.${topicKey}`, { lng: "en" });
  sendEvent({
    event: "gaEvent",
    eventCategory: "Customer Service Contact",
    eventAction: "Contact Form Send",
    eventLabel: label,
  });
}

export function trackSendToSalesPartnerButton(
  salesPartnerNamer: string,
  entries: { designation: string; quantity: number }[],
  wishlistId: string,
) {
  const eventLabel = entries.map(({ designation, quantity }) => `${designation}|${quantity}`).join("§");
  sendEvent({
    event: "gaEvent",
    eventCategory: "Sales Partner Finder",
    eventAction: `Forward wishlist-${salesPartnerNamer}`,
    eventLabel: eventLabel.length > 500 ? "Too long" : eventLabel,
  });
}

export function trackSelectionAssistantRotaryFilters() {
  const params = new URLSearchParams(window.location.search);
  let filterString = "";
  if (params.has("isSelectionAssistant")) {
    if (params.has("assistantValues")) {
      filterString += `Characteristics: ${params.get("assistantValues")} `;
    }

    if (params.has("productGroup")) {
      const productGroupFullString = params.get("productGroup");
      const productGroupAsArray = productGroupFullString!.split("/");
      const indexOfLastElement = productGroupAsArray.length - 1;
      const selectedFilter = productGroupAsArray[indexOfLastElement];
      if (selectedFilter !== "ROTARY") {
        filterString += `Type: ${selectedFilter} `;
      }
    }
  }

  if (filterString !== "") {
    sendEvent({
      event: "gaEvent",
      eventCategory: "Selection Assistant",
      eventAction: "Selection Assistant - Rotary",
      filterUsage: filterString,
    });
  }
}

export function trackLanguageListPDPdownloadTab(languageList: { label: string; value: string }[]) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "PDP Download",
    eventLabel: languageList.map((lang) => lang.label).join(", "),
  });
}

export function trackPublicationOrder(publication: PublicationDownloadFile) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "Downloads - Order medias",
    eventLabel: publication.pubid + " § " + publication.language + " § " + publication.titleRaw,
  });
}
export function trackPublicationDownload(publication: PublicationDownloadFile) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "Downloads - Download",
    eventLabel: publication.pubid + " § " + publication.language + " § " + publication.titleRaw,
  });
}

export function trackSelectedCountrySTC(selectedCountry: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "STC Finder",
    eventAction: "Country search",
    eventLabel: selectedCountry,
  });
}

export function trackPDPDownloadBasicFrequencies(name: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "Basic Frequencies Download",
    eventLabel: name,
  });
}

export function trackPDPDownloadDatasheet() {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "PDP Download Data sheet PDF",
    eventLabel: "Download PDF",
  });
}

export function trackRohsDownloadDatasheet(productDesignation: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "PDP Interactions",
    eventAction: "Material Compliance Download",
    eventLabel: productDesignation,
  });
}

export function trackSearchAutosuggestClicks(name: string, type: string, searchValue: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "Search Autosuggest",
    eventAction: `Searched by ${searchValue}`,
    eventLabel: `Category: ${type}, selected: ${name}`,
  });
}
export function trackSearchAutosuggestLinks(name: string, type: string, searchValue: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "Search Autosuggest",
    eventAction: `Searched by ${searchValue}`,
    eventLabel: `Category: ${name}, selected: ${type}`,
  });
}

export function trackSearchAutosuggestButtonClicks(name: string, buttonType: string, searchValue: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: "Search Autosuggest",
    eventAction: `Searched by ${searchValue}`,
    eventLabel: `${buttonType}, selected: ${name}`,
  });
}

export function trackRegisterForFree(source: string) {
  sendEvent({
    event: "gaEvent",
    eventCategory: source,
    eventAction: `Register for Free`,
    eventLabel: `null`,
  });
}

export function trackLoginAttempt(eventAction = "Sign In") {
  sendEvent({
    event: "gaEvent",
    eventCategory: `Sign In`,
    eventAction: eventAction,
    eventLabel: `Attempt`,
  });
}
export function trackLoginSuccess(eventAction = "Sign In") {
  sendEvent({
    event: "gaEvent",
    eventCategory: `Sign In`,
    eventAction: eventAction,
    eventLabel: `Success`,
  });
}

export function trackCompanyLogo() {
  sendEvent({
    event: "gaEvent",
    eventCategory: "Navigation",
    eventAction: "Logo",
    eventLabel: undefined,
  });
}

export function trackZeroResultTracking(searchValue: string, numberProducts: number, numberResults: number) {
  sendEvent({
    event: "search_performed",
    eventCategory: "Search Interactions",
    eventAction: `Searched by ${searchValue}`,
    push_context: "zero_search_results",
    total_products_results_number: `${numberProducts}`,
    total_informations_results_number: `${numberResults}`,
  });
}

export function trackUnitsInteraction(
  area: "Navigation" | "Search" | "PDP" | "PLP" | "Comparison List" | "SA",
  unitMode: "IMPERIAL" | "METRIC",
) {
  // TODO - Sanity check, clean this up
  const capitalisedUnitMode = unitMode.charAt(0).toUpperCase() + unitMode.slice(1).toLowerCase();
  sendEvent({
    event: "units_and_trees_functionality_interaction",
    eventCategory: "Units Interactions",
    eventAction: `Units`,
    area: `${area}`,
    section: "Unit",
    option_chosen: `${capitalisedUnitMode}`,
  });
}

export function trackTreesInteraction(area: "Navigation" | "Search" | "PLP", viewMode: "Tile" | "List" | "Tree") {
  sendEvent({
    event: "units_and_trees_functionality_interaction",
    eventCategory: "Trees Interactions",
    eventAction: `Trees`,
    area: `${area}`,
    section: "Structure",
    option_chosen: `${viewMode}`,
  });
}

export function trackBearing(clickPlace: "click_on_apply" | "click_on_download_results_table") {
  sendEvent({
    event: "bearing_frequency_calculator_interaction",
    eventCategory: "Bearing Frequency Calculator Interactions",
    eventAction: `Bearing Frequency Calculator Page`,
    push_context: clickPlace,
  });
}

export function trackTabsChangedHSA(
  section: "Bearing & Design Envelope" | "System Characteristics",
  pushContext:
    | "section_switched"
    | "housing_type_selected"
    | "shaft_diameter_provided"
    | "outside_diameter_provided"
    | "item_from_designation_search_dropdown_selected",
  shaftDiameter: { min: string; max: string },
  outsideDiameter: { min: string; max: string },
  housingType?: string,
  itemName?: string,
) {
  sendEvent({
    event: "housing_selection_assistant_interaction",
    eventCategory: "HSA Interactions",
    eventAction: "Housing Selection Assistant Page",
    push_context: pushContext,
    section: section,
    house_type: housingType,
    shaft_diameter: shaftDiameter,
    outside_diameter: outsideDiameter,
    item_name: itemName,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function trackHeaterSelection(isDesignation: boolean, designation: string, attributes?: any) {
  sendEvent({
    event: "Heater Selection Assistant",
    eventCategory: "Heater Selection Assistant Interactions",
    eventAction: `Apply`,
    button_enabled: "true",
    selected_by: isDesignation ? "designation" : "attributes",
    designation: designation ?? "",
    attributes: attributes ?? {},
  });
}

export const trackDmcScan = (scanType: ScanResultType, productCode: string, productId?: string) => {
  const status = getScanStatus(scanType);
  sendEvent({
    event: "one_code_scan_related_interaction",
    push_context: "scan_performed",
    status: status,
    product_code: productCode,
    product_id: productId,
  });
};

export const trackDmcForm = (scanType: ScanResultType, productCode: string, success: boolean) => {
  const status = getScanStatus(scanType);
  sendEvent({
    event: "one_code_scan_related_interaction",
    push_context: "form_submitted",
    origin_status: status,
    product_code: productCode,
    status: success ? "Successful" : "Unsuccessful", // Regarding the form submission state
  });
};

export const trackSubmittedSearch = (
  searchQuery: string,
  totalResults: number,
  totalProductResults: number,
  totalContentResults: number,
) => {
  sendEvent({
    event: "search_related_interaction",
    action: "search_submitted",
    searchQuery: searchQuery,
    total_results: `${totalResults}`,
    total_products_results: `${totalProductResults}`,
    total_content_results: `${totalContentResults}`,
  });
};

export const trackAffiliateCADRedirect = (
  productDesignation: string,
  productId: string,
  partnerName: string,
  partnerId: string,
) => {
  sendEvent({
    event: "cad_related_interaction",
    eventCategory: "CAD Data",
    action: "Partner referral",
    PDP_designation: productDesignation,
    PDP_productID: productId,
    user_came_from_partner: true,
    partner: partnerName,
    partner_id: partnerId,
  });
};

export const trackCadDownload = (
  productDesignation: string,
  productId: string,
  downloadType: string,
  userCameFromPartner: boolean,
  partnerName?: string,
) => {
  sendEvent({
    event: "cad_related_interaction",
    action: "download_performed",
    prod_name: productDesignation,
    prod_id: productId,
    type: downloadType,
    user_came_from_partner: userCameFromPartner,
    partner: partnerName,
  });
};

export const trackSalesPushReportDownload = (type: "csv" | "xlsx") => {
  sendEvent({
    event: "Campaigns_Related_Interaction",
    action: type === "csv" ? "Download CSV" : "Download XLSX",
  });
};

export const trackIntermediatePageLanding = (
  designation: string,
  id: string,
  cameFromPartner: boolean,
  partner?: string,
  partnerId?: string,
) => {
  sendEvent({
    event: "datasheet_related_interaction",
    action: "download_performed",
    prod_name: designation,
    prod_id: id,
    user_came_from_partner: cameFromPartner,
    partner: partner ?? undefined,
    partnerId: partnerId ?? undefined,
  });
};

export const trackBuyFromSalesPartnerButton = (designation: string, code: string, pageType: string) => {
  sendEvent({
    event: "buyFromSalesPartnerButtonClick",
    productName: designation,
    productID: code,
    pageType: pageType,
  });
};

export const trackSalesPartnerPopup = (
  designation: string,
  code: string,
  pageType: GAPageTypeName,
  salesPartner: SalesPartnerStock[],
) => {
  sendEvent({
    event: "salesPartnerPopupShown",
    productName: designation,
    productID: code,
    pageType: pageType,
    sales_partners_listed: salesPartner,
  });
};

export const trackGoToShopSalesPartnerPopUp = (
  designation: string,
  code: string,
  pageType: GAPageTypeName,
  salesPartner: SalesPartnerStock,
) => {
  sendEvent({
    event: "goToShopClickSalesPartnerPopUp",
    productName: designation,
    productID: code,
    pageType: pageType,
    selected_sales_partner: salesPartner,
  });
};

export const trackAddToCartSalesPartnerPopUp = (designation: string, code: string, pageType: GAPageTypeName) => {
  sendEvent({
    event: "addToCartFromSalesPartnerPopup",
    productName: designation,
    productID: code,
    pageType: pageType,
    selected_sales_partner: null,
  });
};

export const trackSalesPartnerPopUpClose = (designation: string, code: string, pageType: GAPageTypeName) => {
  sendEvent({
    event: "salesPartnerPopupClosed",
    productName: designation,
    productID: code,
    pageType: pageType,
  });
};
